<template>
  <div class="app-input" :class="{ 'app-input--block': block }">
    <!-- Label click → manual focus on the el-input ref. EP doesn't
         forward `id` to its internal <input>, so the standard
         <label for=…> association would point at a non-existent
         element (clicks wouldn't focus). Calling el-input's exposed
         focus() preserves a11y behaviour. -->
    <label v-if="label" class="app-input__label" @click="inputRef?.focus()">
      {{ label }}
      <span v-if="required" class="app-input__required">*</span>
    </label>
    <el-input
      ref="inputRef"
      :model-value="modelValue"
      :type="type"
      :placeholder="placeholder"
      :disabled="disabled"
      :readonly="readonly"
      :clearable="clearable"
      :show-password="type === 'password'"
      :maxlength="maxlength"
      :show-word-limit="showWordLimit"
      :rows="rows"
      :size="elSize"
      :prefix-icon="prefixIcon"
      :suffix-icon="suffixIcon"
      :autocomplete="autocomplete"
      :step="type === 'number' ? step : undefined"
      :min="type === 'number' ? min : undefined"
      :max="type === 'number' ? max : undefined"
      :pattern="pattern"
      :name="name"
      @update:model-value="$emit('update:modelValue', $event)"
      @blur="$emit('blur', $event)"
      @focus="$emit('focus', $event)"
      @change="$emit('change', $event)"
      @input="$emit('input', $event)"
    >
      <template v-if="$slots.prefix" #prefix><slot name="prefix" /></template>
      <template v-if="$slots.suffix" #suffix><slot name="suffix" /></template>
      <template v-if="$slots.append" #append><slot name="append" /></template>
    </el-input>
    <p v-if="hint && !error" class="app-input__hint">{{ hint }}</p>
    <p v-if="error" class="app-input__error">{{ error }}</p>
  </div>
</template>

<script setup lang="ts">
/**
 * AppInput — wraps el-input with consistent label + hint + error UX.
 *
 * Use this for all form inputs in the refined-look pass. Native
 * <input> migrations: just replace tag + add v-model.
 *
 * Slots: prefix, suffix, append for el-input's prefix/suffix/append.
 */
type Size = 'sm' | 'md' | 'lg'

const props = withDefaults(defineProps<{
  modelValue?: string | number
  label?: string
  type?: 'text' | 'password' | 'email' | 'number' | 'url' | 'tel' | 'textarea' | 'search'
  placeholder?: string
  hint?: string
  error?: string
  required?: boolean
  disabled?: boolean
  readonly?: boolean
  clearable?: boolean
  maxlength?: number | string
  showWordLimit?: boolean
  rows?: number
  size?: Size
  prefixIcon?: any
  suffixIcon?: any
  block?: boolean
  /** Number-input attributes — passed through to the underlying
   * el-input/input so browser spinner step + min/max guards work.
   * Only applied when type='number'. */
  step?: number | string
  min?: number | string
  max?: number | string
  /** HTML form attributes (autocomplete, pattern, name) — surfaced
   * for native form integrations + password-manager compatibility. */
  autocomplete?: string
  pattern?: string
  name?: string
}>(), {
  type: 'text',
  size: 'md',
  block: true
})

defineEmits<{
  'update:modelValue': [value: string | number]
  'blur': [event: FocusEvent]
  'focus': [event: FocusEvent]
  'change': [value: string | number]
  'input': [value: string | number]
}>()

// Ref to the el-input instance so we can call focus() on label click.
// EP exposes focus(), blur(), select() on its template ref.
const inputRef = ref<any>(null)
// Expose focus()/blur() so parents can imperatively control the input
// the same way they'd do with a native input.
defineExpose({
  focus: () => inputRef.value?.focus(),
  blur: () => inputRef.value?.blur(),
  select: () => inputRef.value?.select()
})

const elSize = computed(() => {
  if (props.size === 'sm') return 'small'
  if (props.size === 'lg') return 'large'
  return 'default'
})
</script>

<style scoped>
.app-input {
  display: inline-block;
}
.app-input--block {
  display: block;
  width: 100%;
}
.app-input__label {
  display: block;
  font-size: 12px;
  font-weight: 500;
  color: var(--text-muted);
  margin-bottom: 6px;
}
.app-input__required {
  color: var(--accent);
  margin-left: 2px;
}
.app-input__hint {
  font-size: 11px;
  color: var(--text-dim);
  margin-top: 4px;
}
.app-input__error {
  font-size: 11px;
  color: var(--accent);
  margin-top: 4px;
}
</style>
